import React, { useEffect, useRef } from "react";
import Layout from "../../../components/corporateAdmin/Layout";
import Helmet from "react-helmet";
import Table from "../../../components/ui/Table";
import usePaginate from "../../../hooks/api/usePaginate";
import apiRequest from "../../../utils/corporateAdmin/apiRequest";
import useModal from "../../../hooks/ui/useModal";
import Pagination from "../../../components/ui/Pagination";
import { FaEdit, FaPlus, FaTrashAlt } from "react-icons/fa";
import CreateCompliance from "../../../components/corporateAdmin/vendorCompliance/CreateCompliance";
import EditCompliance from "../../../components/corporateAdmin/vendorCompliance/EditCompliance";
import DeleteCompliance from "../../../components/corporateAdmin/vendorCompliance/DeleteCompliance";
import Modal from "../../../components/ui/utils/Modal";
import Button from "../../../components/ui/utils/Button";
import useFormInput from "../../../hooks/form/useFormInput";
import InputField from "../../../components/ui/form/InputField";
import { useSelector } from "react-redux";

const VendorCompliance = () => {
  const {
    currentPage,
    data,
    error,
    fetchData,
    limit,
    loading,
    setPage,
    setSorting,
    sort,
    totalItems,
    totalPages,
    setFilters,
  } = usePaginate({
    apiMethod: apiRequest.getVendorCompliance,
    dataKey: "vendorCompliances",
  });

  const { isOpen, openModal, closeModal, actionType } = useModal();

  const handleSort = (field) => {
    const order =
      sort.sortField === field && sort.sortOrder === "asc" ? "desc" : "asc";
    setSorting({ sortField: field, sortOrder: order });
  };

  const searchInitialValues = { vendorName: "" };
  const { values, handleChange, setValues } =
    useFormInput(searchInitialValues);

  const debounceRef = useRef(null);
  useEffect(() => {
    if (values.vendorName !== undefined) {
      if (debounceRef.current) clearTimeout(debounceRef.current);

      debounceRef.current = setTimeout(() => {
        const filters = {};

        if (values.vendorName) {
          filters.vendorName = { $regex: values.vendorName, $options: "i" };
        }

        setFilters(filters);
      }, 500);

      return () => clearTimeout(debounceRef.current);
    }
  }, [values.vendorName]);

  const BRAND_NAME = useSelector(
    (state) => state.globalState?.siteSetting?.site_settings?.site_name
  );

  return (
    <Layout>
      <Helmet>
        <title>Vendor Compliance - {BRAND_NAME ?? ""}</title>
      </Helmet>

      <div className="p-4 bg-white rounded">
        <div className="flex items-center gap-1 py-2">
          <Button
            onClick={() => openModal({ type: "add" })}
            variant="primary"
          >
            <div className="flex items-center gap-3">
              <FaPlus />
              <p>Add Compliance</p>
            </div>
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-[1fr_100px] gap-2 my-2">
          <InputField
            name="vendorName"
            placeholder="Search Vendor"
            value={values.vendorName}
            onChange={handleChange}
          />
          <button
            className="py-1 text-white bg-emerald-600 rounded"
            onClick={() => setValues(searchInitialValues)}
          >
            Clear
          </button>
        </div>

        <Table
          headers={[
            { label: "Vendor", key: "vendorName", sorting: true },
            { label: "Location", key: "location", sorting: false },
            { label: "License", key: "licenseName", sorting: false },
            { label: "Expiry", key: "expiryDate", sorting: false },
            { label: "Status", key: "status", sorting: true },
            { label: "Action", key: "action", sorting: false },
          ]}
          loading={loading}
          handleSort={handleSort}
          sort={sort}
          error={error}
          cols={6}
          renderRow={
            <tbody>
              {data?.map((item) => {
                const isExpired =
                  item.expiryDate &&
                  new Date(item.expiryDate) < new Date();

                return (
                  <tr key={item._id} className="hover:bg-gray-50">
                    <td className="px-3 py-3">{item.vendorName}</td>
                    <td className="px-3 py-3">{item.location}</td>
                    <td className="px-3 py-3">{item.licenseName}</td>
                    <td className="px-3 py-3">
                      {new Date(item.expiryDate).toLocaleDateString()}
                    </td>

                    <td className="px-3 py-3">
                      <span
                        className={`px-2 py-1 rounded text-xs ${
                          isExpired
                            ? "bg-red-100 text-red-600"
                            : "bg-green-100 text-green-700"
                        }`}
                      >
                        {isExpired ? "Expired" : item.status}
                      </span>
                    </td>

                    <td className="px-3 py-3">
                      <div className="flex gap-3 justify-center">
                        <button
                          onClick={() =>
                            openModal({
                              type: "edit",
                              data: { state: item },
                            })
                          }
                          className="text-primary"
                          title="Edit"
                        >
                          <FaEdit size={18} />
                        </button>

                        <button
                          onClick={() =>
                            openModal({
                              type: "delete",
                              data: { state: item },
                            })
                          }
                          className="text-red-500"
                        >
                          <FaTrashAlt size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          }
        />

        {totalPages > 1 && (
          <Pagination
            pagination={{
              currentPage,
              totalPages,
              totalItems,
              perPage: limit,
            }}
            handlePageChange={setPage}
          />
        )}
      </div>

      <Modal isOpen={isOpen} onClose={closeModal} size="lg">
        {actionType?.type === "add" && (
          <CreateCompliance closeModal={closeModal} callback={fetchData} />
        )}

        {actionType?.type === "edit" && (
          <EditCompliance
            data={actionType?.data?.state}
            closeModal={closeModal}
            callback={fetchData}
          />
        )}

        {actionType?.type === "delete" && (
          <DeleteCompliance
            data={actionType?.data?.state}
            closeModal={closeModal}
            callback={fetchData}
          />
        )}
      </Modal>
    </Layout>
  );
};

export default VendorCompliance;
